import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import dayjs from 'dayjs';
import { ReactNode } from 'react';
import type { Option, WorkActivityOption } from '../tasks.types';
import PriorityField from './PriorityField';
import WorkActivityFields, { EMPTY_WORK_ACTIVITY, WorkActivityValue } from './WorkActivityFields';

/**
 * One task's fields, laid out the same way wherever a task is written — the Assign Work
 * screen (one block per task row) and the NEW TASK / edit dialog. Hours are kept as the
 * typed strings; {@link hoursOrNull} turns them into the API's numbers.
 */
export interface TaskFieldsValue {
  title: string;
  priority: string;
  /** Work activity, its type, and — for structural types — Shuttering/Framing and drawings. */
  work: WorkActivityValue;
  description: string;
  /** ISO `yyyy-mm-dd`. */
  startDate: string;
  dueDate: string;
  estimatedHours: string;
  allocatedHours: string;
}

/** A new task: today to today, 8 allocated hours, medium priority. */
export const emptyTaskFields = (): TaskFieldsValue => {
  const today = dayjs().format('YYYY-MM-DD');
  return {
    title: '',
    priority: 'MEDIUM',
    work: EMPTY_WORK_ACTIVITY,
    description: '',
    startDate: today,
    dueDate: today,
    estimatedHours: '',
    allocatedHours: '8',
  };
};

export const hoursOrNull = (v: string) => (v.trim() === '' ? null : Number(v));

/** The first problem with the fields, or null when they can be sent. */
export function taskFieldsProblem(v: TaskFieldsValue): string | null {
  if (!v.title.trim()) return 'A title is required.';
  if (v.startDate && v.dueDate && v.dueDate < v.startDate) return 'Due date cannot be before start date.';
  if ((hoursOrNull(v.estimatedHours) ?? 0) < 0 || (hoursOrNull(v.allocatedHours) ?? 0) < 0) return 'Hours cannot be negative.';
  return null;
}

/** The API body for these fields (create and update share it). */
export function taskFieldsBody(v: TaskFieldsValue, editing: boolean) {
  return {
    title: v.title.trim(),
    description: v.description.trim(),
    priority: v.priority,
    startDate: v.startDate || null,
    dueDate: v.dueDate || null,
    estimatedHours: hoursOrNull(v.estimatedHours),
    allocatedHours: hoursOrNull(v.allocatedHours),
    // On edit, 0 / "" are how the API is told to clear a value.
    workActivityId: v.work.workActivityId ?? (editing ? 0 : null),
    typeOfWorkActivityId: v.work.typeOfWorkActivityId ?? (editing ? 0 : null),
    structuralWorkType: v.work.structuralWorkType ?? (editing ? '' : null),
    structuralDrawings: v.work.structuralDrawings,
  };
}

export default function TaskFieldsBlock({
  value,
  onChange,
  priorities,
  activities,
  projectId,
  action,
}: {
  value: TaskFieldsValue;
  onChange: (next: TaskFieldsValue) => void;
  priorities: Option[];
  activities: WorkActivityOption[];
  /** The task's project; its Work Details decide the structural drawing options. */
  projectId: number | null;
  /** Shown at the end of the title row, e.g. a remove button. */
  action?: ReactNode;
}) {
  const set = <K extends keyof TaskFieldsValue>(key: K, v: TaskFieldsValue[K]) => onChange({ ...value, [key]: v });

  return (
    <Stack gap={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems='center'>
        <TextField label='Title' size='small' required fullWidth value={value.title} onChange={(e) => set('title', e.target.value)} />
        <PriorityField priorities={priorities} value={value.priority} onChange={(v) => set('priority', v)} sx={{ minWidth: 160 }} />
        {action}
      </Stack>
      <WorkActivityFields activities={activities} projectId={projectId} value={value.work} onChange={(work) => set('work', work)} />
      <TextField
        label='Description'
        size='small'
        multiline
        minRows={2}
        fullWidth
        value={value.description}
        onChange={(e) => set('description', e.target.value)}
      />
      <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
        <TextField
          label='Start date'
          type='date'
          size='small'
          fullWidth
          value={value.startDate}
          onChange={(e) => set('startDate', e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label='Due date'
          type='date'
          size='small'
          fullWidth
          value={value.dueDate}
          inputProps={{ min: value.startDate || undefined }}
          onChange={(e) => set('dueDate', e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        {/* Estimated hours are no longer asked; a stored estimate is kept as it is on edit. */}
        <TextField
          label='Allocated hours'
          type='number'
          size='small'
          fullWidth
          inputProps={{ min: 0, step: 0.25 }}
          helperText='Planned evenly over the working days between the dates'
          value={value.allocatedHours}
          onChange={(e) => set('allocatedHours', e.target.value)}
        />
      </Stack>
    </Stack>
  );
}
